import { CademyError } from '@shared/domain-shared';
import { QueryFunction, UseQueryOptions, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

type CourseIdentifier = {
    educatorSlug: string;
    courseSlug: string;
};

export type PaymentOptions = {
    card: { enabled: boolean };
    paypal: { enabled: boolean };
    invoice: { enabled: boolean };
    manual: { enabled: boolean; label?: string; message?: string };
    klarna: { enabled: boolean };
};

const getPaymentOptions = async ({
    educatorSlug,
    courseSlug,
}: CourseIdentifier): Promise<PaymentOptions> => {
    const response = await fetch(
        `${process.env.NEXT_PUBLIC_CADEMY_API_URL}/courses/${educatorSlug}/${courseSlug}/payment-options`,
        {
            credentials: 'include',
            method: 'GET',
        }
    );

    if (!response.ok) {
        if (response.headers.get('content-type') === 'application/problem+json') {
            const problemDetails = await response.json();
            const error = CademyError.fromObject(problemDetails);
            throw error;
        }
        const error = await response.text();
        throw new Error(error);
    }

    return await response.json();
};

type QueryKey = [queryType: 'course-payment-options', educatorSlug: string, courseSlug: string];

const paymentOptionsQuery: QueryFunction<PaymentOptions, QueryKey> = ({ queryKey }) => {
    return getPaymentOptions({
        educatorSlug: queryKey[1],
        courseSlug: queryKey[2],
    });
};

export type UsePaymentOptions = Omit<
    UseQueryOptions<PaymentOptions, unknown, PaymentOptions, QueryKey>,
    'queryKey' | 'queryFn'
>;
export const useCoursePaymentOptions = (
    { educatorSlug, courseSlug }: CourseIdentifier,
    options?: UsePaymentOptions
) => {
    const queryKey = useMemo((): QueryKey => {
        return ['course-payment-options', educatorSlug, courseSlug];
    }, [educatorSlug, courseSlug]);

    return useQuery({
        queryKey,
        queryFn: paymentOptionsQuery,
        ...options,
    });
};
